import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSettings } from 'services/settings/SettingsContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { fetchData } from 'services/api/fetchData';
import { AdditionalModule, INTEGRATION_FEATURES, SubscriptionData } from '../BillingTypes';
import { availableIntegrations } from '../../integrations/availableIntegrations';
import { formatPriceOnUserLocale, getModuleMonthlyPriceForSeats, getTotalEnteredSeats } from '../functions/billingUtils';
import SelectButton from 'components/buttons/SelectButton';
import MultiSelectButton from 'components/buttons/MultiSelectButton';
import '../../../../style/scss/billing.scss';

interface IntegrationModuleCardProps {
    integrationModules: AdditionalModule[];
    currentSubscription: SubscriptionData;
    updatedSubscription: SubscriptionData;
    setUpdatedSubscription: React.Dispatch<React.SetStateAction<SubscriptionData>>;
};

// Consolidated wizard card to select an integration with your current software
const IntegrationModuleCard: React.FC<IntegrationModuleCardProps> = ({
    integrationModules, currentSubscription, updatedSubscription, setUpdatedSubscription
}) => {
    const { t } = useTranslation();
    const { userLocale } = useSettings();
    const { environmentType, handleLogout } = useAuthContext();
    const preselectedRef = useRef(false);

    // Build the selectable integration options with their readable provider names
    const integrationOptions = integrationModules.map(module => {
        const integration = availableIntegrations.find(entry => entry.featureKey === module.feature);
        return { module, label: integration?.name ?? module.feature };
    });

    // Get the selected integration names of the updated subscription
    const selectedIntegrationNames = (updatedSubscription.additional_modules || [])
        .map(module => module.name)
        .filter(name => INTEGRATION_FEATURES.includes(name));

    // Count the integrations which are already active on the current subscription
    const activeIntegrationCount = (currentSubscription.subscription_lines || [])
        .filter(line => line.type === 'additional_module' && line.is_active && INTEGRATION_FEATURES.includes(line.product_name || '')).length;

    // Replace the integration selection while keeping the other modules untouched
    const applyIntegrationSelection = (integrationNames: string[]) => {
        setUpdatedSubscription(prev => {
            const otherModules = (prev.additional_modules || []).filter(module => !INTEGRATION_FEATURES.includes(module.name));
            return { ...prev, additional_modules: [...otherModules, ...integrationNames.map(name => ({ name }))] };
        });
    };

    // Preselect the connected integrations for trials so they are included in the first payment
    useEffect(() => {
        if (environmentType !== 'trial' || preselectedRef.current || selectedIntegrationNames.length > 0) {
            return;
        }
        preselectedRef.current = true;

        // Fetch the connected integrations and map their slugs to the feature keys
        fetchData({ apiUrl: 'get_integration_list', handleLogout }).then(connectedIntegrations => {
            const connectedFeatureKeys = (connectedIntegrations ?? [])
                .map((integration: { slug: string }) => availableIntegrations.find(entry => entry.slug === integration.slug)?.featureKey)
                .filter((featureKey: string | undefined): featureKey is string => !!featureKey && INTEGRATION_FEATURES.includes(featureKey));
            if (connectedFeatureKeys.length > 0) {
                applyIntegrationSelection(connectedFeatureKeys);
            }
        });
    }, [environmentType]);

    // Calculate the total monthly price of the selected integrations for the entered seats
    const totalSeats = getTotalEnteredSeats(updatedSubscription);
    const totalIntegrationPrice = selectedIntegrationNames.reduce((total, name) => {
        const moduleOption = integrationModules.find(module => module.feature === name);
        return moduleOption ? total + getModuleMonthlyPriceForSeats(moduleOption, totalSeats) : total;
    }, 0);

    return (
        <div className='module-row-card integration-module-card'>
            <div className='module-info'>
                <h5>{t('billing.module_price.integration_card_title')}</h5>
                <p>{t('billing.module_price.integration_card_description')}</p>
                {activeIntegrationCount > 1 ? (
                    // Show a multiselect when multiple integrations are already active
                    <MultiSelectButton
                        label='billing.module_price.integration_card_select_label'
                        size='small'
                        items={integrationOptions.map((option, index) => ({ label: option.label, value: index }))}
                        selectedItems={integrationOptions
                            .map((option, index) => selectedIntegrationNames.includes(option.module.feature) ? index : -1)
                            .filter(index => index >= 0)}
                        onSelectionChange={(selectedIndexes) => {
                            applyIntegrationSelection(selectedIndexes.map(index => integrationOptions[index].module.feature));
                        }}
                    />
                ) : (
                    // Show a single select dropdown to keep the flow simple for new customers
                    <SelectButton
                        label='billing.module_price.integration_card_select_label'
                        size='small'
                        items={[
                            { label: 'billing.module_price.integration_card_no_selection_label', value: '' },
                            ...integrationOptions.map(option => ({ label: option.label, value: option.module.feature }))
                        ]}
                        selectedItem={selectedIntegrationNames[0] ?? ''}
                        onSelectionChange={(selected) => {
                            applyIntegrationSelection(selected ? [String(selected)] : []);
                        }}
                    />
                )}
            </div>
            {selectedIntegrationNames.length > 0 && (
                <div className='module-price'>
                    <div className='price-label'>
                        <span className='currency-icon'>€ </span>
                        {formatPriceOnUserLocale(totalIntegrationPrice, userLocale)}
                        <span className='price-period'> {t('billing.module_price.per_month_suffix')}</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default IntegrationModuleCard;
